/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {ClientUserProvider, ReportProvider} from '../../../../../providers';
import {ClientUser, Report, ReportEngageResult, CheckListEntityFinancials, User, Helpers, ReportClientType, ReportClientUser, PurchaseAgreementType} from '../../../../../model';
import {ReportService} from '../../../../../services/report.service';
import {StageService, UserService, ModeService,LogService} from '../../../../../services';
import { PreviewPdfModal } from '../preview-pdf/preview-pdf.modal';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import { FinancialType } from 'src/app/model/enums/financial.type';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as moment from 'moment';
import { Router } from '@angular/router';
import * as FileSaver from 'file-saver';
import { SystemType } from 'src/app/model/enums/system.type';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import { PreviewSize } from 'src/app/model/enums/preview.size';
import { ConfirmationDialogComponent, InputSearchComponent } from 'src/app/components/fields';
import { SearchableItem } from 'src/app/model/interface';
import { PresetValue } from 'src/app/model/preset-value';
Quill.register('modules/blotFormatter', BlotFormatter);
@Component({
  selector: 'engage-report-manual-modal',
  templateUrl: './engage-report-manual.modal.html',
  styleUrls: ['./engage-report-manual.modal.scss']
})
export class EngageReportManualModal implements OnInit, OnDestroy {
  @ViewChild('recipientField', { static: true }) recipientField: InputSearchComponent;

  subject: Subject<boolean>;
  subscription: any;
  isResend: boolean;
  isChanges: boolean = false;

  currentReport: Report;
  private reportSub: any;

  isLoading = false;
  isLoadingPreview = false;
  htmlSubject = 'Name of Company (dba)';
  recipients: any[] = [];
  cc: string[] = [];
  bcc: string[] = [];
  userTypes: PresetValue[] = [];

  preview: BsModalRef;
  PreviewSize = PreviewSize;
  size: PreviewSize = PreviewSize.MEDIUM;
  confirmRef: BsModalRef;
  editorStyle = { 'height': '400px' };

  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '25rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no'
  };

  htmlContent = '';
  htmlHead = '';
  htmlBody = '';
  user: User;
  currentMode: SystemType;
  modeSub: any;
  disclaimer: string = `<div dir="ltr" style="text-size-adjust: auto;">
                <span style="caret-color: rgb(0, 0, 0); color: rgb(0, 0, 0); font-family: arial, helvetica, sans-serif; font-size: 12.8px; text-size-adjust: auto;">
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                The information transmitted herewith is sensitive information intended only for use to the individual or entity to which it is addressed. &nbsp;</span>
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution,
                copying or other use of, or taking of any action in reliance upon, this information is strictly prohibited.&nbsp; If you have received this communication in error,
                please contact the sender and delete the material from your computer.</span></span>
            </div>`;

  constructor(
    private modalService: BsModalService,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private stageService: StageService,
    private userService: UserService,
    private router: Router,
    private modeService: ModeService,
    public userProvider: ClientUserProvider,
    private logService : LogService
  ) {}


  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc') {
        if (!this.isLoading)
          this.closePreview();
      }
    });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      if (currentReport) {
        this.currentReport = currentReport;
        if (this.currentReport != null)
          if (this.currentReport.ID) {
            this.user = this.userService.getCurrentUser();

            const nonPrimEntity: string = this.currentReport.getNonPrimaryEntities(', ');

            if (this.currentReport.OVERRIDE != null && this.currentReport.OVERRIDE === true) {
              this.htmlSubject = `${this.currentReport.primaryEntity()}`;
            } else {
              this.htmlSubject = `${this.currentReport.primaryEntity()}${this.currentReport.primaryEntityDba ? ' (dba ' + this.currentReport.primaryEntityDba + ')' : ''}${nonPrimEntity ? nonPrimEntity : ''}`;
            }

            if(this.currentReport.ENG_LETTER_CONTENT_DS)
              this.htmlContent =  this.currentReport.ENG_LETTER_CONTENT_DS;
            else
              this.constructHtml();

            this.setRecipient();
          }
      }
    });

    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  ngOnDestroy(): void {
    if (this.subscription)
      this.subscription.unsubscribe();

    if (this.reportSub)
      this.reportSub.unsubscribe();

    if(this.modeSub)
      this.modeSub.unsubscribe();
  }

  onConfirm(): void {
    this.isLoading = true;
    if (this.recipients?.length > 0 && this.htmlSubject.length > 0) {
      let defaultSig = `<div style="text-align:left;font-weight:bold;" dir="ltr">${this.user.getName()}</div>`;
      defaultSig += '<div style="text-align:left;margin-bottom:3px;" dir="ltr">Reliant Business Valuation</div>';
      defaultSig += '<img src="https://www.reliantvalue.com/wp-content/uploads/2017/07/Updated-small.jpg" width="130px"/><br/>';
      defaultSig += '<p style="text-align:left;font-family:Helvetica,Arial,sans-serif;font-size:12px;margin-bottom:0;margin-top:3px;color:#5F5F5F;line-height:135%;">';
      defaultSig += '<div>Your Most Reputable Source for SBA Values&reg; </div>';
      defaultSig += '<div>1246 S. River Road, Ste 101 | Cranbury, NJ 08512 </div>';
      defaultSig += '<div>Office: (908) 888-6030  | Fax: (908) 888-6040 </div>';
      defaultSig += '<div><a href="www.reliantvalue.com">www.reliantvalue.com</a></div></p>';
      defaultSig += '<br/>';

      let signature = this.user.SIGNATURE ? this.user.SIGNATURE + '<br/>' : defaultSig;
      let content = this.htmlContent + signature + this.disclaimer;

      const body = {
        recipients: this.recipients,
        subject: this.htmlSubject,
        email: btoa(content),
        cc: this.cc,
        bcc: this.bcc,
      };

      if(this.isResend){
        this.reportProvider.resendManual(this.currentReport.ID, body, this.currentMode)
        .subscribe(data => {
          this.isLoading = false;
          if (data.success) {
            this.showSuccessDialog();

          } else this.logService.error(JSON.stringify(data, null, 2));
        }, error => {
          this.isLoading = false;
          this.logService.error(error);
        });
      }
      else {
        this.reportProvider.engageReportManual(this.currentReport.ID, body, this.currentMode)
        .subscribe(data => {
          this.isLoading = false;
          if (data.success) {
            const engageResult = ReportEngageResult.parse(data.result);

            this.reportService.addStageHistory(engageResult.HISTORY_STAGE);
            this.currentReport.STAGE = this.stageService.getStageById(engageResult.HISTORY_STAGE.SID);
            this.currentReport.ASSIGNEE = engageResult.HISTORY_STAGE.ASSIGNEE;
            this.currentReport.STATE = engageResult.HISTORY_STAGE.STATE;

          /*   this.currentReport.DOCUMENT_ID = engageResult.DOCUMENT_ID;
            this.currentReport.DOCUMENT_STATE = engageResult.DOCUMENT_STATE; */
            this.currentReport.DATE_ENGAGED_LETTER = moment(engageResult.DATE_ENGAGED_LETTER).format('MMM DD, YYYY');

            this.showSuccessDialog();

          } else this.logService.error(JSON.stringify(data, null, 2));
        }, error => {
          this.isLoading = false;
          this.logService.error(error);
        });
      }
    }
  }

  closeModal(): void {
    this.subject.next(false);
    this.subject.complete();
  }

  gotoProfile(): void {
    this.router.navigate(['/account']);
  }

  onPreviewPdf(): void {

    const config = Helpers.getModalConfig();
    config.class = 'modal-lg';
    this.isLoadingPreview = true;

    this.reportProvider.getEngagementLetterPreview(this.currentReport.ID, this.currentMode)
      .subscribe(response => {
        this.isLoadingPreview = false;
        // const blob = new Blob([response], { type: 'application/pdf' });
        const src =  new Uint8Array(response);

        this.preview = this.modalService.show(PreviewPdfModal, config);
        const ackSubject = new Subject<Boolean>();
        this.preview.content.subject = ackSubject;
        this.preview.content.name =  this.currentReport.primaryEntity();
        this.preview.content.source = src;


        ackSubject.subscribe(result => {
          if (result) {this.logService.log(result); }
        });

      }, error => {
        this.isLoadingPreview = false;
        this.logService.error(error);
      });
  }

  onDownloadPdf(): void {
    this.isLoadingPreview = true;
    if (this.currentReport.DOCUMENT_ID) {
      this.reportProvider.getDocuSignDocument(this.currentReport.ID)
        .subscribe(response => {
          this.isLoadingPreview = false;
          const blob = new Blob([response], { type: 'application/pdf' });

          if (this.currentMode === SystemType.EA) {
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - ${this.currentReport.primaryEntity()}.pdf`);
          }
          else {
            FileSaver.saveAs(
              blob, 'Reliant Business Valuation - Engagement Letter.pdf');
          }

        }, error => {
          this.isLoadingPreview = false;
          this.logService.error(error);
        });
    } else {
      this.reportProvider.getEngagementLetterPreview(this.currentReport.ID, this.currentMode)
        .subscribe(response => {
          this.isLoadingPreview = false;
          const blob = new Blob([response], { type: 'application/pdf' });

          if (this.currentMode === SystemType.EA) {
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - ${this.currentReport.primaryEntity()}.pdf`);
          }
          else {
            FileSaver.saveAs(
              blob, 'Reliant Business Valuation - Engagement Letter.pdf');
          }

        }, error => {
          this.isLoadingPreview = false;
          this.logService.error(error);
        });
    }

  }

  getSigner(): ClientUser {
    if (this.currentReport.CLIENT_USERS) {
      for (const user of this.currentReport.CLIENT_USERS) {
        if (user.EMAIL_ENGAGE)
          return user.CLIENT_USER;
      }
    }

    return null;
  }

  private constructHtml(): void {
    let section3 = '';
    let section4 = '';
    let section5 = '';

    // Requesting Federal Tax Return
    let cYear = new Date().getFullYear();
    let currentDate = new Date();

    // atleaast 3 months
    let mos3 = moment(currentDate).subtract(3, 'months');
    // 6 months old as of the current date
    let mos6 = moment(currentDate).add(3, 'months');

    // =================== SECTION 1 ========================= //
    this.htmlHead = `<p id="userName">${this.getSigner()?.getName()},</p><br/>`;

    // =================== SECTION 2 ========================= //
    let buff = '<p>The files for the above mentioned company have been reviewed and we are in receipt of the following documents:</p>';
    if (this.currentReport.CHECK_LIST) {
      buff += '<ul>';
      for (const c of this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST) {

        const fedTaxReturn: CheckListEntityFinancials[] = [];
        const internal: CheckListEntityFinancials[] = [];
        const basis: string[] = [];
        const bsbasis: string[] = [];

        if (c.ENTITY_CHECK_DATE) {
          // buff += `<b>${c.ENTITY.NAME}</b>`;
          // buff += `<ul>`;

          for (const financials of c.FINANCIALS.sort((a, b) => a.YEAR - b.YEAR)) {
            if (financials.TYPE === FinancialType.FEDERAL_TAX_RETURN) {
              fedTaxReturn.push(financials);
            } else if (financials.TYPE === FinancialType.INTERNAL) {
              internal.push(financials);
            }

            if (financials && financials.IS_BASIS) {
              if (!basis.find(bs => bs === financials.IS_BASIS.toLowerCase())) {
                basis.push(financials.IS_BASIS.toLowerCase());
              }
            }

            if (financials && financials.BS_BASIS) {
              if (!bsbasis.find(bs => bs === financials.BS_BASIS.toLowerCase())) {
                bsbasis.push(financials.BS_BASIS.toLowerCase());
              }
            }
          }

          /* let basis = 'cash/accrual/hybrid';
          if (fedTaxReturn.length > 0) {
            if (typeof fedTaxReturn[0] !== 'undefined') {
              if (fedTaxReturn[0].IS_BASIS) {
                if (fedTaxReturn[0].IS_BASIS.toLowerCase() === 'CASH'.toLowerCase())
                  basis = 'cash';
                else if (fedTaxReturn[0].IS_BASIS.toLowerCase() === 'ACCRUAL'.toLowerCase())
                  basis = 'accrual';
                else if (fedTaxReturn[0].IS_BASIS.toLowerCase() === 'HYBRID'.toLowerCase())
                  basis = 'hybrid';
              }

              if (fedTaxReturn.length === 1) {
                buff += `<li>${fedTaxReturn[0].YEAR} federal tax return - ${basis}</li>`;
              } else { // If Multiple FTR
                buff += `<li>${fedTaxReturn.length} years federal tax return (${fedTaxReturn.map(item => item.YEAR).join(', ')}) - ${basis}</li>`;
              }
            }
          } */

          /* if (fedTaxReturn.length > 0) {
            for (const cef of fedTaxReturn) {
              if (cef && cef.IS_BASIS) {
                if (!basis.find(bs => bs === cef.IS_BASIS.toLowerCase())) {
                  basis.push(cef.IS_BASIS.toLowerCase());
                }
              }

              if (cef && cef.BS_BASIS) {
                if (!bsbasis.find(bs => bs === cef.BS_BASIS.toLowerCase())) {
                  bsbasis.push(cef.BS_BASIS.toLowerCase());
                }
              }
            }
          }
        */

          if (fedTaxReturn.length === 1) {
            buff += `<li>${fedTaxReturn[0].YEAR} federal tax return - ${basis.join(', ')}</li>`;
          } else if (fedTaxReturn.length > 1) { // If Multiple FTR
            let isFYE = moment(fedTaxReturn[0].DATE).month() == 11 && moment(fedTaxReturn[0].DATE).date() == 31 ? true : false;
            buff += `<li>${fedTaxReturn.length} years federal tax return (${!isFYE ? 'FYE ' : ''} ${fedTaxReturn.map(item => item.YEAR).join(', ')}) - ${basis.join(', ')}</li>`;
          }

          if (internal.length > 0) {
            for (const itr of internal) {
              if (c.BS_TOGGLE) {
                if (moment(c.ENTITY_CHECK_DATE).valueOf() === moment(c.BS_ENTITY_CHECK_DATE).valueOf()) {
                  buff += `<li>${itr.INTERIM === true ? 'interim ' : ''} income statement and balance sheet dated ${moment(itr.DATE).format('MM.DD.YY')} -  ${basis.join(', ')}</li>`;
                }
                else {
                  buff += `<li>${itr.INTERIM === true ? 'interim ' : ''} income statement dated ${moment(itr.DATE).format('MM.DD.YY')} -  ${basis.join(', ')}</li>`;
                  buff += `<li>${itr.INTERIM === true ? 'interim ' : ''} balance sheet dated ${moment(itr.BS_DATE).format('MM.DD.YY')} -  ${bsbasis.join(', ')}</li>`;
                }
              }
              else {
                buff += `<li>${itr.INTERIM === true ? 'interim ' : ''} income statement and balance sheet dated ${moment(itr.DATE).format('MM.DD.YY')} -  ${basis.join(', ')}</li>`;
              }

            }
          }

          if (c.AR) {
            buff += `<li>AR aging summary dated ${moment(c.AR_DATE).format('MM.DD.YY')}</li>`;
          }

          if (c.AP) {
            buff += `<li>AP aging summary dated ${moment(c.AP_DATE).format('MM.DD.YY')}</li>`;
          }
        }
      }

      // Purchase Agreement
      if (this.currentReport.CHECK_LIST.P_PA === true) {
        if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE == PurchaseAgreementType.STOCK) {
          buff += '<li>stock purchase agreement</li>';
        }
        else if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE == PurchaseAgreementType.ASSET) {
          buff += '<li>asset purchase agreement</li>';
        }
        else {
          buff += '<li>purchase agreement</li>';
        }
      }
      // LOI Intent to Purchase
      if (this.currentReport.CHECK_LIST.P_LOIP === true) {
        if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE == PurchaseAgreementType.STOCK) {
          buff += '<li>LOI to purchase stock</li>';
        }
        else if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE == PurchaseAgreementType.ASSET) {
          buff += '<li>LOI to purchase asset</li>';
        }
      }

      if (this.currentReport.CHECK_LIST.RE_OWNED === true) {
        if (this.currentReport.CHECK_LIST.RE_OWNED_APPRAISAL) {
          buff += '<li>real estate appraisal dated. The processor will later manually type in the date of the appraisal as this information is found after looking through the document</li>';
        }
      }

      if (this.currentReport.CHECK_LIST.CWU === true) {
        buff += '<li>credit write up</li>';
      }

      if (this.currentReport.CHECK_LIST.CL_LOIF === true) {
        buff += '<li>commitment letter</li>';
      }

      if (this.currentReport.CHECK_LIST.LOIF === true) {
        buff += '<li>LOI to finance</li>';
      }

      if (this.currentReport.CHECK_LIST.FIN_FORECAST === true) {
        buff += '<li>financial projections</li>';
      }

      if (this.currentReport.CHECK_LIST.ORDER_FORM === true) {
        buff += '<li>RBV order form</li>';
      }

      if (this.currentReport.CHECK_LIST.BVQ_FORM === true) {
        buff += '<li>RBV Questionnaire</li>';
      }

      if (this.currentReport.CHECK_LIST.BUSINESS_PLAN === true) {
        buff += '<li>business plan</li>';
      }

      if (this.currentReport.CHECK_LIST.FDD === true) {
        buff += '<li>FDD</li>';
      }

      // Custom Documents
      if (this.currentReport.CHECK_LIST.CUSTOM_DOCUMENTS && this.currentReport.CHECK_LIST.CUSTOM_DOCUMENTS.length > 0) {
        for (let doc of this.currentReport.CHECK_LIST.CUSTOM_DOCUMENTS) {
          if (doc.VALUE === true) {
            buff += `<li>${doc.NAME}</li>`;
          }
        }
      }

      buff += '</ul>';

      // =================== SECTION 3 ========================= //
      if (this.currentReport.CHECK_LIST) {
        for (const c of this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST) {
          const basis: string[] = [];
          const bsbasis: string[] = [];

          if (c.ENTITY_CHECK_DATE) {
            for (const financials of c.FINANCIALS.sort((a, b) => a.YEAR - b.YEAR)) {

              if (financials && financials.IS_BASIS) {
                if (!basis.find(bs => bs === financials.IS_BASIS.toLowerCase())) {
                  basis.push(financials.IS_BASIS.toLowerCase());
                }
              }

              if (financials && financials.BS_BASIS) {
                if (!bsbasis.find(bs => bs === financials.BS_BASIS.toLowerCase())) {
                  bsbasis.push(financials.BS_BASIS.toLowerCase());
                }
              }
            }

            let year1 = c.FINANCIALS.find(f => f.YEAR === (cYear - 3));
            let year2 = c.FINANCIALS.find(f => f.YEAR === (cYear - 2));
            let year3 = c.FINANCIALS.find(f => f.YEAR === (cYear - 1));
            let interim = c.FINANCIALS.find(f => f.INTERIM === true);

            // 2017 federal tax return, 2018 federal tax return, 2019 federal tax return, and balance sheet as of 06.30.20
            // All accrual financials
            if (year1 && (year1.BS_TYPE && year1.BS_TYPE == FinancialType.FEDERAL_TAX_RETURN)) {
              if (year2 && (year2.BS_TYPE && year2.BS_TYPE == FinancialType.FEDERAL_TAX_RETURN)) {
                if (year3 && (year3.BS_TYPE && year3.BS_TYPE == FinancialType.FEDERAL_TAX_RETURN)) {
                  if (c.BS_TOGGLE) {
                    if (interim && moment(interim.BS_DATE).isBetween(mos3, mos6)) {
                      if (bsbasis.length == 1 && bsbasis[0] === 'accrual') {
                        if (interim.TYPE !== FinancialType.INTERNAL)
                          section3 += `<li><span style="background-color: yellow">interim income statement through ${moment(c.ENTITY_CHECK_DATE).format('MM.DD.YY')} - accrual</span></li>`;
                      }
                    }
                  }
                }
              }
            }

            /*  if (year3 && (!year3.TYPE || year3.TYPE == FinancialType.NOT_AVAILABLE)) {
               if ((year1 && year1.TYPE === FinancialType.FEDERAL_TAX_RETURN) && (year2 && year2.TYPE == FinancialType.FEDERAL_TAX_RETURN)) {
                 if (interim && moment(interim.DATE).isBetween(mos3, mos6)) {
                   section3 += `<li><span style="background-color: yellow">${cYear - 1} federal tax return</span></li>`;
                 }
               }
             } */

            // income statement and balance sheet dated 12.31.17 - cash,
            // income statement and balance sheet dated 12.31.19 - cash,
            // income statement and balance
            let internal = c.FINANCIALS.filter(f => f.TYPE === FinancialType.INTERNAL);
            if (internal && internal.length > 1) {
              for (let f of c.FINANCIALS) {
                if (!f.INTERIM) {
                  if (f && (!f.IS_BASIS || !f.TYPE)) {
                    section3 += `<li><span style="background-color: yellow">income statement and balance sheet dated ${moment(f.DATE).format('MM.DD.YY')} - cash</span></li>`;
                  }
                }
              }
            }

            // 2017 federal tax return, 2018 federal tax return, and income statement and balance sheet dated 07.04.20
            let fedTaxReturn = c.FINANCIALS.filter(f => f.TYPE === FinancialType.FEDERAL_TAX_RETURN);
            if (fedTaxReturn && fedTaxReturn.length > 1) {
              for (let f of c.FINANCIALS) {
                if (!f.INTERIM) {
                  if (f && (!f.IS_BASIS || !f.TYPE)) {
                    if (interim && moment(interim.DATE).isBetween(mos3, mos6)) {
                      section3 += `<li><span style="background-color: yellow"> ${f.YEAR} federal tax return</span></li>`;
                    }
                  }
                }
              }
            }

            //2017 federal tax return, 2018 federal tax return,
            // 2019 federal tax return, and income statement and balance sheet dated 06.30.20
            //Tax returns are cash interims are accrual
            if (year1 && (year1.TYPE && year1.TYPE == FinancialType.FEDERAL_TAX_RETURN) && (year1.IS_BASIS && year1.IS_BASIS.toLowerCase() === 'cash')) {
              if (year2 && (year2.TYPE && year2.TYPE == FinancialType.FEDERAL_TAX_RETURN) && (year1.IS_BASIS && year2.IS_BASIS.toLowerCase() === 'cash')) {
                if (year3 && (year3.TYPE && year3.TYPE == FinancialType.FEDERAL_TAX_RETURN) && (year3.IS_BASIS && year3.IS_BASIS.toLowerCase() === 'cash')) {
                  if (interim && moment(interim.DATE).isBetween(mos3, mos6) && interim.IS_BASIS.toLowerCase() === 'accrual') {
                    section3 += '<li><span style="background-color: yellow"> processor will confirm with financial analyst </span></li>';
                  }
                }
              }
            }

            //income statement and balance sheet dated 12.31.17,
            //income statement and balance sheet dated 12.31.18,
            //income statement and balance sheet dated 12.31.19,
            //income statement and balance sheet dated 01.31.20
            //All cash financials
            //(interims report less than 3 months of financials)
            if (year1 && (year1.TYPE && year1.TYPE == FinancialType.INTERNAL) && (year1.IS_BASIS && year1.IS_BASIS.toLowerCase() === 'cash')) {
              if (year2 && (year2.TYPE && year2.TYPE == FinancialType.INTERNAL) && (year2.IS_BASIS && year2.IS_BASIS.toLowerCase() === 'cash')) {
                if (year3 && (year3.TYPE && year3.TYPE == FinancialType.INTERNAL) && (year3.IS_BASIS && year3.IS_BASIS.toLowerCase() === 'cash')) {
                  if (interim && moment(interim.DATE).isBetween(mos3, mos6)) {
                    if (interim.IS_BASIS.toLowerCase() === 'cash') {
                      section3 += `<li><span style="background-color: yellow"> more recent income statement and balance sheet for ${cYear} - cash</span></li>`;
                    }
                  }
                }
              }
            }
          }
        }

        if (this.currentReport.CLIENT && this.currentReport.CLIENT.IS_BANK !== true)
          if (this.currentReport.CHECK_LIST.P_NA !== true) {
            if (this.currentReport.CHECK_LIST.P_PA !== true && this.currentReport.CHECK_LIST.P_LOIP !== true) {
              section3 += '<li><span style="background-color: yellow">purchase agreement <b>OR</b> LOI to purchase</span></li>';
              section3 += '<ul><li><span style="background-color: yellow"><i>if not available, please confirm whether this will be an asset or stock (equity) sale</i></span></li></ul>';
            }
          }

        // For Non-Bank
        if (this.currentReport.CLIENT && this.currentReport.CLIENT.IS_BANK === true) {
          if (this.currentReport.PURPOSE == 'Refinance') {
            section3 += '<li><span style="background-color: yellow">confirmation on whether you would like an asset or stock (equity) value</span></li>';
          }
        }
      }

      if (section3.length !== 0) {
        buff += '<br/><p><b>We will need the following in order to provide you with an engagement letter:</b></p>';
        buff += '<ul>';
        buff += section3;
        buff += '</ul>';
      }

      // =================== SECTION 4 ========================= //
      if (this.currentReport.CHECK_LIST) {
        for (const c of this.currentReport.CHECK_LIST.ENTITY_CHECK_LIST) {
          const basis: string[] = [];
          const bsbasis: string[] = [];

          if (c.ENTITY_CHECK_DATE) {
            for (const financials of c.FINANCIALS.sort((a, b) => a.YEAR - b.YEAR)) {

              if (financials && financials.IS_BASIS) {
                if (!basis.find(bs => bs === financials.IS_BASIS.toLowerCase())) {
                  basis.push(financials.IS_BASIS.toLowerCase());
                }
              }

              if (financials && financials.BS_BASIS) {
                if (!bsbasis.find(bs => bs === financials.BS_BASIS.toLowerCase())) {
                  bsbasis.push(financials.BS_BASIS.toLowerCase());
                }
              }
            }

            /*  if (fedTaxReturn && fedTaxReturn.length > 0) {

               let year1 = fedTaxReturn.find(f => f.YEAR === (cYear - 3));
               let year2 = fedTaxReturn.find(f => f.YEAR === (cYear - 2));
               let year3 = fedTaxReturn.find(f => f.YEAR === (cYear - 1));

               if (!year1) {
                 buff += `<li>${cYear - 3} federal tax return</li>`;
               }

               if (year1 && year2 && year3 && basis.length === 1 && basis[0] === 'accrual') {
                 buff += `<li>interim balance sheet as of ${moment(c.ENTITY_CHECK_DATE).format('MM.DD.YY')} - accrual</li>`;
               }
             } */

            let year1 = c.FINANCIALS.find(f => f.YEAR === (cYear - 3));
            let year2 = c.FINANCIALS.find(f => f.YEAR === (cYear - 2));
            let year3 = c.FINANCIALS.find(f => f.YEAR === (cYear - 1));
            let interim = c.FINANCIALS.find(f => f.INTERIM === true);

            if (year1 && (!year1.TYPE || year1.TYPE == FinancialType.NOT_AVAILABLE)) {
              if ((year2 && year2.TYPE === FinancialType.FEDERAL_TAX_RETURN) && (year3 && year3.TYPE == FinancialType.FEDERAL_TAX_RETURN)) {
                if (interim && moment(interim.DATE).isBetween(mos3, mos6)) {
                  section4 += `<li>${cYear - 3} federal tax return</li>`;
                }
              }
            }

            if ((year1 && year1.TYPE === FinancialType.NOT_AVAILABLE) && (year2 && year2.TYPE === FinancialType.NOT_AVAILABLE)) {
              if ((year3 && year3.IS_BASIS.toLowerCase() === 'cash') && (interim && interim.IS_BASIS.toLowerCase() === 'cash')) {
                section4 += '<li>financial projections; No historical financials</li>';
              }
            }

            if (year1 && year1.TYPE === FinancialType.FEDERAL_TAX_RETURN) {
              if (year2 && year2.TYPE === FinancialType.FEDERAL_TAX_RETURN) {
                if (year3 && year3.TYPE === FinancialType.FEDERAL_TAX_RETURN) {
                  if (interim && moment(interim.DATE).isBetween(mos3, mos6)) {
                    if (basis.length == 1 && basis[0] === 'accrual') {
                      section4 += `<li>interim balance sheet as of ${moment(c.ENTITY_CHECK_DATE).format('MM.DD.YY')} - accrual</li>`;
                    }
                  }
                }
              }
            }

          }
        }

        let lender = this.getClientUserByType(ReportClientType.PROCESSOR);
        if (!lender) {
          section4 += '<li>name, phone number <b><u>and</u></b> email of the person who can provide additional information about the company</li>';
        }
        else if (this.currentReport.CLIENT_USERS && this.currentReport.CLIENT_USERS.length > 0) {
          if (lender) {
            if (!lender.CLIENT_USER.NAME && !lender.CLIENT_USER.PHONE_MOBILE && !lender.CLIENT_USER.PHONE_OFFICE && !lender.CLIENT_USER.PHONE_PERSONAL && !lender.CLIENT_USER.EMAIL) {
              section4 += '<li>name, phone number <b><u>and</u></b> email of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.NAME && !lender.CLIENT_USER.PHONE_MOBILE && !lender.CLIENT_USER.PHONE_OFFICE && !lender.CLIENT_USER.PHONE_PERSONAL) {
              section4 += '<li>name <b><u>and</u></b> phone number of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.NAME && !lender.CLIENT_USER.EMAIL) {
              section4 += '<li>name <b><u>and</u></b> email of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.PHONE_MOBILE && !lender.CLIENT_USER.PHONE_OFFICE && !lender.CLIENT_USER.PHONE_PERSONAL && !lender.CLIENT_USER.EMAIL) {
              section4 += '<li>phone number <b><u>and</u></b> email of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.NAME) {
              section4 += '<li>name of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.PHONE_MOBILE && !lender.CLIENT_USER.PHONE_OFFICE && !lender.CLIENT_USER.PHONE_PERSONAL) {
              section4 += '<li>phone number of the person who can provide additional information about the company</li>';
            }
            else if (!lender.CLIENT_USER.EMAIL) {
              section4 += '<li>email of the person who can provide additional information about the company</li>';
            }
          }
        }

        if (this.currentReport.CHECK_LIST.RE_OWNED) {
          if (this.currentReport.CHECK_LIST.RE_OWNED_APPRAISAL !== true || this.currentReport.CHECK_LIST.RE_OWNED_APPRAISAL_CONTACT !== true) {
            section4 += '<li>real estate appraisal <b>OR</b> real estate appraiser\'s contact information</li>';
          }
        }
      }

      if (section4.length !== 0) {
        buff += '<br/><p><b>We will need the following within 2 business days of engagement to help expedite the valuation process:</b></p>';
        buff += '<ul>';
        buff += section4;
        buff += '</ul>';
      }

      // =================== SECTION 5 ========================= //
      if (this.currentReport.CHECK_LIST) {
        if (this.currentReport.CHECK_LIST.CWU === true) {
          section5 += '<li>credit write up<ul><li>if available, this document will help us avoid duplicating efforts when requesting information about the business</li></ul></li>';
        }

        if (this.currentReport.CHECK_LIST.CL_LOIF === true && this.currentReport.CHECK_LIST.LOIF === true) {
          section5 += '<li>commitment letter <b>OR</b> LOI to finance</li>';
        }

        if (this.currentReport.CHECK_LIST.FIN_FORECAST === true) {
          section5 += '<li>financial projections</li>';
        }

        if (this.currentReport.CHECK_LIST.P_NA === true) {
          if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE === PurchaseAgreementType.ASSET) {
            section5 += '<li>asset purchase agreement OR LOI to purchase assets</li>';
          }
          else if (this.currentReport.CHECK_LIST.PURCHASE_AGREEMENT_TYPE === PurchaseAgreementType.STOCK) {
            section5 += '<li>stock purchase agreement OR LOI to purchase stock</li>';
          }
        }
      }

      if (section5.length !== 0) {
        buff += '<br/><p><b>We will need the following, <i>if available</i>:</b></p>';
        buff += '<ul>';
        buff += section5;
        buff += '</ul>';
      }
    }

    if (section3.length === 0) {
      if(this.currentReport.CLIENT && this.currentReport.CLIENT.NEW_BANK === true)
        buff += '<br/><p>This order is ready to be engaged. The engagement letter will be uploaded to ValuTrac, kindly sign and return at your earliest convenience. Please note we cannot begin work on the appraisal until this letter is countersigned.</p>';
      else
        buff += '<br/><p>This order is ready to be engaged. The engagement letter is attached to this email, kindly sign and return at your earliest convenience. Please note we cannot begin work on the appraisal until this letter is countersigned.</p>';
    }

    buff += '<br/>';
    buff += '<p>Please let me know if you have any questions. Thank you.</p><br/>';

    this.htmlBody = buff;
    this.htmlContent = this.htmlHead += this.htmlBody;
  }s

  getRecipientName(client: string): void {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(this.htmlHead, 'text/html');

    this.htmlHead = htmlDoc.getElementById('userName').innerHTML = `<span dir="ltr" id="userName">${client},</span><br/><br/>`;

    this.htmlContent = this.htmlHead += this.htmlBody;
  }

  getClientUserByType(type: ReportClientType): ReportClientUser {
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS)
        if (cl.TYPE === type) {
          return cl;
        }

    }
    return null;
  }

  expandArea(): void{
   if(this.size === PreviewSize.MEDIUM){
      this.size = PreviewSize.LARGE;
      this.editorStyle = { 'height': 'auto' };
    } else {
      this.size = PreviewSize.MEDIUM;
      this.editorStyle = { 'height': '400px' };
    }
  }

  showSuccessDialog(): void {
    const config = Helpers.getModalConfig();
    config.class = 'modal-dialog-centered';

    this.confirmRef = this.modalService.show(ConfirmationDialogComponent, config);
    const ackSubject = new Subject<any>();
    this.confirmRef.content.subject = ackSubject;
    this.confirmRef.content.title = 'Success';
    this.confirmRef.content.text = 'Your email has been successfully sent!';
    this.confirmRef.content.actionButton = 'Ok';
    this.confirmRef.content.showCancel = false;

    ackSubject.subscribe(value => {
      if(value){
        this.subject.next(true);
        this.subject.complete();
      }
    });
  }

  recipientChanged(recs: SearchableItem[]): void {
    this.recipients = [];

    if(recs != null){
      this.recipients = recs.map(r =>({ NAME: r.getValue().NAME, EMAIL: r.getValue().EMAIL }));
    }
  }

  ccChanged(recs: SearchableItem[]): void {
    this.cc = [];
    if (recs != null) {
      this.cc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }

  bccChanged(recs: SearchableItem[]): void {
    this.bcc = [];

    if (recs != null) {
      this.bcc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }

  resetTemplate(): void {
    this.constructHtml();
    this.setRecipient();
  }

  private setRecipient(): void {
    this.recipientField.arrValue = [];
    this.recipients = [];

    for (const rcu of this.currentReport.CLIENT_USERS) {
      if (rcu.TYPE === ReportClientType.LSP) {
        this.recipientField.arrValue.push(rcu.CLIENT_USER);

        this.recipients.push({
          EMAIL: rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : "",
          NAME: rcu.CLIENT_USER.NAME ? rcu.CLIENT_USER.NAME : rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : ""
        });

      } else {
        if (rcu.CLIENT_USER != null && (rcu.EMAIL_ENGAGE === true || rcu.EMAIL_CC)) {
          this.recipientField.arrValue.push(rcu.CLIENT_USER);

           this.recipients.push({
            EMAIL: rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : "",
            NAME: rcu.CLIENT_USER.NAME ? rcu.CLIENT_USER.NAME : rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : "'"
          });
        }
      }

    }
  }

  onContentChanged(value): void {
    this.isChanges = true;
  }

  closePreview(): void{
    if(this.isChanges){
      const config = Helpers.getModalConfig();
      config.class = 'modal-dialog-centered';

      this.confirmRef = this.modalService.show(ConfirmationDialogComponent, config);
      const ackSubject = new Subject<any>();
      this.confirmRef.content.subject = ackSubject;
      this.confirmRef.content.title = 'Confirm';
      this.confirmRef.content.text = 'Are you sure you want to save the latest changes?';
      this.confirmRef.content.actionButton = 'Yes';
      this.confirmRef.content.cancelButton = 'No'
      this.confirmRef.content.actionButtonClass = '';

      ackSubject.subscribe(value => {
        if (value) {
          this.reportProvider.save(this.currentReport.ID, 'ENG_LETTER_CONTENT_DS', this.htmlContent)
            .subscribe(data => {
              if(data.success){
                this.currentReport.ENG_LETTER_CONTENT_DS = this.htmlContent;

                this.closeModal();
              }
            }, err => {
              this.logService.log(err);
            });
        }
        else {
          this.closeModal();
        }
      });
    }
    else {
      this.closeModal();
    }
  }
}
